import { FileKey } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge, Skeleton } from '../../../components/ui';
import { useDuongDan } from '../../../hooks/useDuongDan';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { soVN } from '../../../lib/dinh-dang';
import { CAU_DEM_NGUOC, TRANG_THAI_CUC_BO } from '../constants';
import { useDuocXemLicenseCucBo, useLicenseCucBo } from '../hooks/useLicenseCucBo';
import { demNguocLicense } from '../nghiep-vu/license-cuc-bo';
import { DUONG_DAN_LICENSE_CUC_BO } from './BangCanhBaoLicense';

/**
 * Thẻ license ở chân thanh điều hướng (bản on-premise): tên gói, badge trạng
 * thái và số ngày tới mốc kế. Cả thẻ dẫn sang màn license.
 *
 * Dùng chung truy vấn với màn license và băng cảnh báo, nên không thêm lần gọi
 * API nào. Chỉ quản trị viên bản cài thấy (`useDuocXemLicenseCucBo`); bản SaaS
 * không dựng gì.
 *
 * Lỗi tải mà chưa có dữ liệu cũ thì hiện một dòng mờ, không tô đỏ: băng cảnh
 * báo đã chọn im lặng khi lỗi để không làm người dùng hoảng, nhưng thẻ này là
 * chỗ người quản trị quen nhìn — biến mất không lời thì họ không biết vì sao.
 * Lỗi chi tiết nằm ở màn license.
 */
export function TheLicenseThanhBen({ collapsed }: { collapsed: boolean }) {
  const { t, locale } = useT();
  const duongDan = useDuongDan();
  const duocXem = useDuocXemLicenseCucBo();
  const q = useLicenseCucBo(duocXem);

  if (!duocXem) return null;

  const khung = cn(
    'mx-2 mt-1.5 flex shrink-0 rounded-lg border border-default transition-colors hover:bg-subtle',
    collapsed ? 'flex-col items-center gap-1 px-1 py-2' : 'items-start gap-2.5 px-2.5 py-2',
  );
  const denManLicense = duongDan(DUONG_DAN_LICENSE_CUC_BO);

  if (!q.data) {
    if (q.isError) {
      const nhan = t('licenseCucBo.thanhBen.loiTai');
      return (
        <Link to={denManLicense} className={khung} aria-label={collapsed ? nhan : undefined} title={collapsed ? nhan : undefined}>
          <FileKey size={collapsed ? 18 : 16} className="mt-px shrink-0 text-content-muted" aria-hidden />
          {!collapsed && <span className="min-w-0 flex-1 text-xs text-content-muted">{nhan}</span>}
        </Link>
      );
    }
    return (
      <div className={cn(khung, 'hover:bg-transparent')}>
        <Skeleton className={collapsed ? 'h-5 w-5' : 'h-4 w-4'} />
        {!collapsed && (
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-24" />
            <Skeleton className="h-3 w-32" />
          </div>
        )}
      </div>
    );
  }

  const dem = demNguocLicense(q.data);
  const cau = CAU_DEM_NGUOC[dem.loai];
  const demNguoc = t(dem.soNgay === 1 && cau.mot ? cau.mot : cau.nhieu, { so: soVN(dem.soNgay, locale) });
  const plan = q.data.license?.plan;

  if (dem.loai === 'CHUA_KICH_HOAT' || !plan) {
    return (
      <Link
        to={denManLicense}
        className={khung}
        aria-label={collapsed ? demNguoc : undefined}
        title={collapsed ? demNguoc : undefined}
      >
        <FileKey size={collapsed ? 18 : 16} className="mt-px shrink-0 text-warning-600" aria-hidden />
        {!collapsed && <span className="min-w-0 flex-1 text-sm font-medium text-content-primary">{demNguoc}</span>}
      </Link>
    );
  }

  const tt = TRANG_THAI_CUC_BO[q.data.status];
  const nhanTrangThai = t(tt.khoa);
  const tomTat = t('licenseCucBo.thanhBen.tomTat', { goi: plan, trangThai: nhanTrangThai, demNguoc });

  if (collapsed) {
    return (
      <Link to={denManLicense} className={khung} aria-label={tomTat} title={tomTat}>
        <FileKey size={18} className="shrink-0 text-content-secondary" aria-hidden />
        {/* Số ngày ở tông trạng thái; trạng thái không đếm ngược thì chỉ còn dấu chấm cùng tông. */}
        <Badge variant={tt.variant} className="px-1.5 text-2xs">
          {dem.coDem ? soVN(dem.soNgay, locale) : '•'}
        </Badge>
      </Link>
    );
  }

  return (
    <Link to={denManLicense} className={khung}>
      <FileKey size={16} className="mt-0.5 shrink-0 text-content-secondary" aria-hidden />
      <span className="sidebar-reveal min-w-0 flex-1">
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          {/* Tên gói là dữ liệu của license, hiện nguyên văn. */}
          <span className="min-w-0 truncate text-sm font-semibold leading-tight text-content-primary">
            {t('licenseCucBo.thanhBen.goi', { goi: plan })}
          </span>
          <Badge variant={tt.variant}>{nhanTrangThai}</Badge>
        </span>
        <span className="mt-1 block text-xs text-content-muted">{demNguoc}</span>
      </span>
    </Link>
  );
}
